import { useQuery } from '@sequencehq/api/utils'
import {
  Box,
  Text,
  CustomerLogo,
  GreyGrey70,
  Link,
  Pill,
  PillProps,
  PopoverWrapper,
  PreviewCard,
  SequenceMenu,
  Skeleton
} from '@sequencehq/core-components'
import { Primary13Regular } from '@sequencehq/core-components'
import { formatAddress, getPortalDomain } from '@sequencehq/utils'
import { HOURS } from '@sequencehq/utils/dates'
import { Customer } from 'modules/Customer/types'
import { EditCustomerModalForm } from 'components/EditCustomer'
import { getLogoSrc } from 'Customer/utils/logo'
import { getEnvironment } from 'lib/environment/environment'
import { FC, SyntheticEvent, useState } from 'react'
import { match } from 'ts-pattern'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { ArchiveCustomerMenuItem } from 'Customers/index'
import { NO_ADDRESS_LABEL } from 'modules/Customers/data'
import { usePermissions } from 'lib/rbac'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

type PreviewCardCustomer = Pick<
  Customer,
  'id' | 'legalName' | 'addresses' | 'archivedAt' | 'domain'
>

type BaseProps = {
  showBorder?: boolean
  showOptions?: boolean
  fullWidth?: boolean
}
type Props =
  | (BaseProps & {
      customerId: string
    })
  | (BaseProps & {
      customer: PreviewCardCustomer
    })

function shouldLoadData(
  props: Props
): props is BaseProps & { customerId: string } {
  return 'customerId' in props
}

const useCustomerForPreviewCard = (
  props: Props
): {
  data: PreviewCardCustomer | null | undefined
  isLoading: boolean
} => {
  const shouldLoadDataFromApi = shouldLoadData(props)

  const { data, isLoading } = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: shouldLoadDataFromApi ? props.customerId : ''
    },
    {
      enabled: shouldLoadDataFromApi,
      staleTime: 24 * HOURS,
      gcTime: 24 * HOURS
    }
  )

  if (!shouldLoadDataFromApi) {
    return { data: props.customer, isLoading: false }
  }

  return { data, isLoading }
}

type CustomerPreviewCardState = 'default' | 'edit'
export const CustomerPreviewCard: FC<Props> = props => {
  const { data: customer, isLoading } = useCustomerForPreviewCard(props)
  const { can } = usePermissions()

  const portalDomain = getPortalDomain(getEnvironment())

  const [state, setState] = useState<CustomerPreviewCardState>('default')

  if (isLoading) {
    return (
      <Skeleton
        width={props.fullWidth ? '100%' : '330px'}
        height="150px"
        borderRadius="8px"
      />
    )
  }

  return (
    <>
      <CustomerPreviewCardUI
        customer={customer}
        showBorder={props.showBorder}
        showOptions={props.showOptions}
        fullWidth={props.fullWidth}
        onClickViewInPortal={() => {
          if (customer) {
            window.open(`${portalDomain}/customers/${customer.id}`, '_blank')
          }
        }}
        onClickEdit={() => {
          setState('edit')
        }}
      />
      {match(state)
        .with('edit', () => {
          if (!customer || !can('org.customer:update')) {
            return null
          }
          return (
            <EditCustomerModalForm
              customerId={customer.id}
              onClose={() => setState('default')}
            />
          )
        })
        .otherwise(() => null)}
    </>
  )
}

export const CustomerPreviewCardPill: FC<
  Props & {
    variant?: PillProps['variant']
    alternativeName?: string
    'data-testid'?: string
  }
> = ({ variant, alternativeName, ...props }) => {
  const { data: customer, isLoading } = useCustomerForPreviewCard(props)

  if (!customer && isLoading) {
    return <Skeleton width="100px" height="24px" borderRadius="8px" />
  }

  if (!customer) {
    return null
  }

  const alternativeNameSuffix =
    alternativeName && alternativeName !== customer.legalName
      ? ` (${alternativeName})`
      : ''
  const customerName = `${customer?.legalName ?? 'Unknown'}${alternativeNameSuffix}`

  return (
    <PopoverWrapper
      popover={<CustomerPreviewCard {...props} />}
      offset={[-4, 4]}
      openDelay={850}
    >
      <Link.Unstyled
        to={`/customers/${customer.id}`}
        onClick={e => e.stopPropagation()}
        style={{ textDecoration: 'none' }}
      >
        <CustomerPreviewCardPillUI
          logoSrc={getLogoSrc(customer)}
          name={customerName}
          variant={variant}
          data-testid={props['data-testid']}
        />
      </Link.Unstyled>
    </PopoverWrapper>
  )
}

export const CustomerPreviewCardPillStatic: FC<{
  customerId: string
  customerName: string
  variant?: PillProps['variant']
  alternativeName?: string
  'data-testid'?: string
  showBorder?: boolean
  showOptions?: boolean
  fullWidth?: boolean
}> = ({ customerId, customerName, variant, alternativeName, ...props }) => {
  // Load customer data asynchronously just for the logo
  const { data: customer, isLoading: isLoadingLogo } = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: customerId },
    { enabled: !!customerId, staleTime: 24 * HOURS, gcTime: 24 * HOURS }
  )

  const alternativeNameSuffix =
    alternativeName && alternativeName !== customerName
      ? ` (${alternativeName})`
      : ''
  const displayName = `${customerName}${alternativeNameSuffix}`

  return (
    <PopoverWrapper
      popover={<CustomerPreviewCard customerId={customerId} {...props} />}
      offset={[-4, 4]}
      openDelay={850}
    >
      <Link.Unstyled
        to={`/customers/${customerId}`}
        onClick={e => e.stopPropagation()}
        style={{ textDecoration: 'none' }}
      >
        <CustomerPreviewCardPillUI
          name={displayName}
          variant={variant}
          logoSrc={customer ? getLogoSrc(customer) : undefined}
          isLoadingLogo={isLoadingLogo}
          data-testid={props['data-testid']}
        />
      </Link.Unstyled>
    </PopoverWrapper>
  )
}

const CustomerPreviewCardUI: FC<{
  customer: PreviewCardCustomer | null | undefined
  onClickViewInPortal: () => void
  onClickEdit: () => void
  showBorder?: boolean
  showOptions?: boolean
  fullWidth?: boolean
}> = ({
  customer,
  onClickViewInPortal,
  onClickEdit,
  showBorder = false,
  showOptions = true,
  fullWidth = false
}) => {
  const { can } = usePermissions()
  if (!customer) {
    return null
  }

  const address = formatAddress(getBillingAddress(customer.addresses))

  return (
    <PreviewCard
      data-testid="customerPreviewCard"
      title={`${customer.legalName}${customer.archivedAt ? ' (archived)' : ''}`}
      subtitle={
        <Box data-testid="customerPreviewCard.address">
          {address.length > 0 ? (
            address.map((addressPart, i) => (
              <Text key={i} {...Primary13Regular} lineHeight="18px">
                {addressPart}
              </Text>
            ))
          ) : (
            <Text {...Primary13Regular} lineHeight="18px" color={GreyGrey70}>
              {NO_ADDRESS_LABEL}
            </Text>
          )}
        </Box>
      }
      fullWidth={fullWidth}
      logoSrc={getLogoSrc(customer)}
      showBorder={showBorder}
      button={
        showOptions
          ? {
              label: 'View customer',
              to: `/customers/${customer.id}`
            }
          : undefined
      }
      kebabMenu={
        showOptions ? (
          <SequenceMenu
            data-testid="customerPreviewCard.menu"
            items={[
              {
                label: 'View in customer portal',
                onClick: withoutPropagation(onClickViewInPortal),
                'data-testid': 'customerPreviewCard.menu.viewInPortal'
              },
              // Divider only shows alongside the edit item. Without update
              // permission, edit and archive are both hidden, so it isn't needed.
              ...(can('org.customer:update')
                ? [
                    {
                      label: 'Edit customer',
                      onClick: withoutPropagation(onClickEdit),
                      'data-testid': 'customerPreviewCard.menu.editCustomer',
                      divider: true
                    }
                  ]
                : []),
              ({ setIsOpen }) => (
                <ArchiveCustomerMenuItem
                  customerId={customer.id}
                  onClose={() => setIsOpen?.(false)}
                />
              )
            ]}
          />
        ) : undefined
      }
    />
  )
}

const withoutPropagation = (fn: () => void) => (e: SyntheticEvent) => {
  e.stopPropagation()
  fn()
}

const CustomerPreviewCardPillUI: FC<{
  name: string
  logoSrc?: string
  isLoadingLogo?: boolean
  variant?: PillProps['variant']
  'data-testid'?: string
}> = ({ name, logoSrc, isLoadingLogo, variant, 'data-testid': dataTestId }) => {
  return (
    <Pill
      icon={
        isLoadingLogo ? (
          <div
            style={{
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Skeleton width="16px" height="16px" borderRadius="50%" />
          </div>
        ) : (
          <CustomerLogo src={logoSrc} />
        )
      }
      interactive
      variant={variant}
      data-testid={dataTestId}
    >
      {name}
    </Pill>
  )
}
